import { useState, useEffect, useCallback } from 'react';
import type { InventoryItem } from '../types/inventory.types';
import { InventoryService } from '../services';
import type { InventoryAdvancedFilters } from '../services/inventoryService';

const parseBoolValue = (value: unknown): boolean => {
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return false;
};

/**
 * SQL API InventoryItemDto -> InventoryItem (camelCase ve PascalCase alanlar). Saf; faturalarda barkodla
 * ürün eklerken de kullanılır.
 */
export function mapInventoryDto(dto: any): InventoryItem {
  // Canlı/yerel backend sürümlerinde alan adları camelCase veya PascalCase dönebiliyor.
  // Parent-child ayrımında yanlış listeleme olmaması için iki formatı da destekliyoruz.
  const parentItemId = dto.parentItemId ?? dto.ParentItemId ?? null;
  const mainItemId = dto.mainItemId ?? dto.MainItemId ?? null;
  const isVariantParent = parseBoolValue(dto.isVariantParent ?? dto.IsVariantParent);
  const warehouseStocks = dto.warehouseStocks ?? dto.WarehouseStocks ?? [];

  return {
    id: dto.id,
    name: dto.name || '',
    barcode: dto.barcode || '',
    categoryId: dto.categoryId || null,
    brandId: dto.brandId || null,
    stockCode: dto.stockCode || '',
    unit: dto.unit || 'adet',
    purchasePrice: dto.purchasePrice || 0,
    purchaseCurrency: dto.purchaseCurrency || 'TRY',
    salePrice: dto.salePrice || 0,
    saleCurrency: dto.saleCurrency || 'TRY',
    vatRate: dto.vatRate || 0,
    vatStatus: dto.vatStatus || 'excluded',
    sctRate: dto.sctRate || 0,
    sctStatus: dto.sctStatus || 'excluded',
    description: dto.description || '',
    desi: dto.desi ?? dto.Desi ?? undefined,
    isActive: dto.isActive !== undefined ? dto.isActive : true,
    createdAt: dto.createdAt || new Date().toISOString(),
    branchesId: dto.branchesId || null,
    parentItemId,
    // LEGACY UYUM: isMainItem alanini DB otoritesi olarak kullanmiyoruz.
    // Gercek hiyerarsi kaynagi parentItemId + isVariantParent'dir.
    // Bu alan sadece eski ekranlarla uyum icin parentItemId'den turetilir.
    isMainItem: !parentItemId,
    isVariantParent,
    canBeNegative: parseBoolValue(dto.canBeNegative),
    departmentNo: dto.departmentNo !== undefined ? dto.departmentNo : null,
    imageUrl: dto.imageUrl || undefined,
    isQRMenu: dto.isQRMenu || false, // ✅ Backend'den gelen isQRMenu değerini kullan
    qrMenuSentAt: dto.qrMenuSentAt || undefined,
    qrMenuLastUpdate: dto.qrMenuLastUpdate || undefined,
    // ✅ YENİ SİSTEM: WarehouseStocks
    warehouseStocks,
    variants: dto.variants || null,
    // Ek alanlar
    listPrice: (dto as any).listPrice || undefined,
    listCurrency: (dto as any).listCurrency || 'TRY',
    isEcommerce: dto.isEcommerce || false,
    ecommerceActivatedAt: dto.ecommerceActivatedAt || undefined,
    ecommerceDeactivatedAt: dto.ecommerceDeactivatedAt || undefined,
    isN11: dto.isN11 || false,
    isKiosk: dto.isKiosk || false,
    n11SentAt: dto.n11SentAt || undefined,
    kioskSentAt: dto.kioskSentAt || undefined,
    kioskLastUpdate: dto.kioskLastUpdate || undefined,
    kioskRemovedAt: dto.kioskRemovedAt || undefined,
    qrMenuRemovedAt: dto.qrMenuRemovedAt || undefined,
    mainItemId,
    printerGroup: dto.printerGroup || undefined,
    // Ek fiyat alanları
    purchasePrice2: (dto as any).purchasePrice2 || undefined,
    purchaseCurrency2: (dto as any).purchaseCurrency2 || 'TRY',
    purchaseVendor: (dto as any).purchaseVendor || undefined,
    purchaseVendor2: (dto as any).purchaseVendor2 || undefined,
    purchasePrice3: (dto as any).purchasePrice3 || undefined,
    purchaseCurrency3: (dto as any).purchaseCurrency3 || 'TRY',
    purchaseVendor3: (dto as any).purchaseVendor3 || undefined,
    salePrice2: (dto as any).salePrice2 || undefined,
    saleCurrency2: (dto as any).saleCurrency2 || 'TRY',
    saleCustomer: (dto as any).saleCustomer || undefined,
    saleCustomer2: (dto as any).saleCustomer2 || undefined,
    salePrice3: (dto as any).salePrice3 || undefined,
    saleCurrency3: (dto as any).saleCurrency3 || 'TRY',
    saleCustomer3: (dto as any).saleCustomer3 || undefined,
    minStockLevel: (dto as any).minStockLevel || undefined,
    maxStockLevel: (dto as any).maxStockLevel || undefined,
    // ✅ YENİ: Şube bazlı fiyatlandırma
    useBranchPricing: dto.useBranchPricing === true,
    // Menşei / yerli malı
    isDomesticProduct: (dto as any).isDomesticProduct === true,
    countryOfOrigin: (dto as any).countryOfOrigin ?? null,
    // Tartı
    isScale: (dto as any).isScale === true,
    scaleCode: (dto as any).scaleCode || undefined,
    scaleSentAt: (dto as any).scaleSentAt || undefined,
    scaleLastUpdate: (dto as any).scaleLastUpdate || undefined,
    scaleRemovedAt: (dto as any).scaleRemovedAt || undefined,
    shelfLifeDays: (dto as any).shelfLifeDays ?? undefined,
    trackLots: (dto as any).trackLots === true || (dto as any).TrackLots === true,
    trackExpiry: (dto as any).trackExpiry === true || (dto as any).TrackExpiry === true,
    itemClass: (dto as any).itemClass ?? (dto as any).ItemClass ?? null,
    productionMode: (dto as any).productionMode ?? (dto as any).ProductionMode ?? 'SALE_EXPLODE',
  };
}

/**
 * Envanter (inventory items) verilerini yöneten custom hook
 * ✅ YENİ: Server-side pagination desteği
 * @param companyId - Şirket ID'si
 * @param warehouseId - (Opsiyonel) Depo filtresi
 * @returns Envanter ürünleri ve yükleme durumu
 */
export const useInventoryData = (companyId: string | null, warehouseId?: string | null) => {
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0); // ✅ Toplam ürün sayısı

  /**
   * SQL API'den InventoryItemDto'yu InventoryItem tipine çevirir
   */
  const mapDtoToInventoryItem = useCallback((dto: any): InventoryItem => mapInventoryDto(dto), []);

  /**
   * ✅ YENİ: Server-side pagination ile envanter ürünlerini SQL API'den yükler
   * @param companyId - Şirket ID'si
   * @param pageNumber - Sayfa numarası (1'den başlar)
   * @param pageSize - Sayfa başına ürün sayısı
   * @param searchTerm - Arama terimi (opsiyonel)
   * @param categoryId - Kategori filtresi (opsiyonel)
   * @param brandId - Marka filtresi (opsiyonel)
   * @param warehouseId - Depo filtresi (opsiyonel)
   * @param isActive - Aktiflik filtresi (opsiyonel)
   * @param isEcommerce - E-ticaret filtresi (opsiyonel)
   * @param sortNewestFirst - Sıralama yönü (opsiyonel) - true: yeniden eskiye, false: eskiden yeniye
   * @param hideZeroPrice - Satış fiyatı 0 olanları gizle (opsiyonel)
   * @param vatRate - KDV oranı filtresi (opsiyonel)
   * @param departmentNo - ÖKC (Departman No) filtresi (opsiyonel)
   */
  const loadInventoryItemsPaged = useCallback(async (
    companyId: string,
    pageNumber: number = 1,
    pageSize: number = 50,
    searchTerm?: string,
    categoryId?: string | null,
    brandId?: string | null,
    warehouseId?: string | null,
    isActive?: boolean | null,
    isEcommerce?: boolean | null,
    sortNewestFirst?: boolean,
    hideZeroPrice?: boolean,
    vatRate?: number | null,
    departmentNo?: number | null,
    sortColumn?: string | null,
    sortDirection?: string | null,
    // Varyant child kayıtlarını ana listeden hariç tut.
    // Default: true (sadece parent ürünler). "Alt varyantları da göster" toggle'ı ile false yapılır.
    excludeVariantChildren: boolean = true,
    advanced?: InventoryAdvancedFilters
  ) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📦 [INVENTORY HOOK] Server-side pagination ile ürünler yükleniyor...', { 
        companyId, pageNumber, pageSize, warehouseId, sortNewestFirst, hideZeroPrice, vatRate, departmentNo, sortColumn, sortDirection, excludeVariantChildren
      });
      
      // SQL API'den sayfalı ürünleri al
      const response = await InventoryService.getPagedItemsSQL(
        companyId,
        pageNumber,
        pageSize,
        searchTerm,
        categoryId,
        brandId,
        warehouseId,
        isActive,
        isEcommerce,
        sortNewestFirst,
        hideZeroPrice,
        vatRate,
        departmentNo,
        sortColumn,
        sortDirection,
        excludeVariantChildren,
        advanced
      );
      
      console.log('✅ [INVENTORY HOOK] SQL\'den alınan ürün sayısı:', response.items.length);
      console.log('📊 [INVENTORY HOOK] Toplam ürün sayısı:', response.totalCount);

      // DTO'ları InventoryItem tipine çevir
      const itemsList: InventoryItem[] = response.items.map(dto => mapDtoToInventoryItem(dto));
      
      // State'i güncelle
      setInventoryItems(itemsList);
      setTotalCount(response.totalCount);
      setLoading(false);
      
      console.log('✅ [INVENTORY HOOK] Ürünler başarıyla yüklendi');
      
    } catch (error) {
      console.error('❌ [INVENTORY HOOK] SQL\'den stok yükleme hatası:', error);
      setLoading(false);
      setError('Stok verileri yüklenirken bir hata oluştu.');
    }
  }, [mapDtoToInventoryItem]);

  return {
    inventoryItems,
    setInventoryItems, // Dışarıdan state'i güncelleyebilmek için
    loading,
    error,
    totalCount, // ✅ Toplam ürün sayısı
    loadInventoryItemsPaged, // ✅ Server-side pagination ile yükleme fonksiyonu
  };
};

